<script>
import { mapGetters } from 'vuex'
import ResourceSearchInput from './ResourceSearchInput.vue'
import TrackResource from '@/resources/track'
import UserResource from '@/resources/user'
import CarResource, { CarSkinResource } from '@/resources/car'
import { AssistResource, RacePresetResource, RacePresetUserResource, WeatherPresetResource } from '@/resources/launcher.js'
import EventFormSessions from './EventFormSessions.vue'
import CalendarResource from '@/resources/calendar'

export default {
    components: {
        ResourceSearchInput,
        EventFormSessions,
    },
    props: {
        event: {
            type: Object,
            required: true,
        },
        party: {
            type: Object,
            required: false,
        },
        calendarId: String,
        calendarOnly: Boolean,
        noSave: Boolean,
        noControls: Boolean,
        tabs: Boolean,
    },
    computed: {
        ...mapGetters(['userResource']),
        resources() {
            return {
                TrackResource,
                CarResource,
                UserResource,
                AssistResource,
                RacePresetResource,
                WeatherPresetResource,
            }
        },
        members() {
            if (!this.party) {
                return []
            }

            const members = this.party.rel('members').resources

            return members
                .slice()
                .sort((a, b) => {
                    const hostDiff = (b.attributes.is_host === true) - (a.attributes.is_host === true)

                    if (hostDiff !== 0) {
                        return hostDiff
                    }

                    return a.attributes.readonly_values.calendar_name.localeCompare(b.attributes.readonly_values.calendar_name)
                })
                .map((member) => {
                    member.entry = this.getEntryForCalendar(member.attributes.calendar)
                    return member
                })
        },
        entries() {
            return this.event.attributes.entries
        },
        hasEntries() {
            return this.entries.length > 0
        },
        hostMember() {
            return this.members.find((member) => member.attributes.is_host)
        },
        hostHasControl() {
            if (this.calendarId) {
                const memberRecordForCalendarId = this.members.find((member) => member.attributes.calendar == this.calendarId)
                if (!memberRecordForCalendarId.attributes.is_host) {
                    return true
                }
            }

            return false
        },
        calendarsIndexed() {
            const indexObj = {}

            for (let calendar of this.calendars) {
                indexObj[calendar.id] = calendar
            }

            return indexObj
        },
        userPresetsIndexed() {
            const indexObj = {}

            for (let preset of this.userPresets) {
                if (!indexObj[preset.attributes.user]) {
                    indexObj[preset.attributes.user] = [preset]
                } else {
                    indexObj[preset.attributes.user].push(preset)
                }
            }

            return indexObj
        },
        userPresetsEditing() {
            if (this.userPresetsEditingCalendarId) {
                const calendar = this.calendarsIndexed[this.userPresetsEditingCalendarId]
                return this.userPresetsIndexed[this.userPresetsEditingUser]
            }

            return null
        },
        myEntry() {
            if (!this.calendarId) return null
            return this.getEntryForCalendar(this.calendarId)
        },
        myPresets() {
            if (!this.myEntry || !this.myEntry.user) return []
            return this.userPresetsIndexed[this.myEntry.user] || []
        },
        sessionStartedAtDisplay() {
            if (!this.sessionStartedAt) return null
            return new Date(this.sessionStartedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        },
        sessionElapsedDisplay() {
            if (!this.sessionStartedAt) return null
            const elapsed = Math.floor(this.sessionElapsed / 1000)
            const hours = Math.floor(elapsed / 3600)
            const minutes = Math.floor((elapsed % 3600) / 60)
            return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`
        },
    },
    watch: {
        party() {
            this.loadCalendars()
        },
    },
    data() {
        return {
            loading: true,
            hasChanges: false,
            serverRunning: false,
            calendars: [],
            sessions: [],
            sessionDurations: null,
            memberStates: {},
            seatedData: {},
            seatedDataIntervalId: null,
            userPresets: [],
            userPresetsExistForHost: false,
            userPresetsEditingUser: null,
            userPresetsEditingCalendarId: null,
            loggedInUserPresets: [],
            loggedInUserPresetsModalVisible: false,
            sessionStartedAt: null,
            sessionElapsed: 0,
            sessionElapsedIntervalId: null,
            usersChanged: false,
        }
    },
    methods: {
        async loadCalendars() {
            if (!this.party) {
                this.calendars = []
                return
            }

            const memberManager = this.party.rel('members')

            if (memberManager) {
                await memberManager.resolve()
            }

            // rest-resource returns an already-resolved CalendarResource instead of a
            // RelatedManager once a member's calendar relation has been resolved before.
            const calendarManagersPromises = this.party.get('members.calendar').map((managerOrResource) => {
                if (!managerOrResource) {
                    return null
                }

                if ('function' === typeof managerOrResource.resolve) {
                    return managerOrResource.resolve()
                }

                return [managerOrResource]
            })

            const newCalendars = await Promise.all(calendarManagersPromises).then((items) => items.flat().filter(Boolean))
            const newIds = new Set(newCalendars.map((calendar) => calendar.id))

            for (let calendar of this.calendars) {
                if (!newIds.has(calendar.id)) {
                    calendar.socketClose()
                }
            }

            for (let calendar of newCalendars) {
                await calendar.socketConnect()

                if (!calendar._eventFormListenerAttached) {
                    calendar._eventFormListenerAttached = true
                    calendar.emitter.on('message', (event) => this.onCalendarMessage(calendar, event))
                }
            }

            this.calendars = newCalendars
        },
        async load() {
            await this.loadCalendars()

            if (this.event.emitter) {
                this.event.emitter.on('server', (isRunning) => (this.serverRunning = isRunning))
            }

            this.sessions = this.event.attributes.sessions

            if (this.party) {
                const hostMember = this.members.find((m) => m.attributes.is_host)
                if (hostMember) {
                    const hostEntry = this.getEntryForCalendar(hostMember.attributes.calendar)
                    if (hostEntry && hostEntry.user) {
                        await this.fetchPresetsForUser(hostEntry.user, true)
                    }
                }
            }

            await this.fetchLoggedInUserPresets()

            this.loadSessionTimer()

            await this.getSeatedData()
            this.seatedDataIntervalId = setInterval(() => this.getSeatedData(), 1e4)
            this.loading = false
        },
        onTabActivated(newTabIndex) {
            if (!this.tabs) {
                return
            }
            const tabsComponent = this.$refs.eventTabs
            const activeTab = tabsComponent && tabsComponent.tabs && tabsComponent.tabs[newTabIndex]
            const role = activeTab && activeTab.$el && activeTab.$el.dataset ? activeTab.$el.dataset.tabRole : null

            if (role === 'racers') {
                this.syncRacerStates()
            } else if (role === 'sessions') {
                this.syncSessionState()
            }
        },
        syncRacerStates() {
            // Every sim re-pushes its current selection (car / user / assists) over
            // the calendar socket so the Racers tab is up to date on every switch.
            for (let calendar of this.calendars) {
                if (calendar && typeof calendar.socketSendRacerStateQuery === 'function') {
                    calendar.socketSendRacerStateQuery()
                }
            }
        },
        syncSessionState() {
            // Only the host owns the sessions, so we ask the host sim.
            if (!this.hostMember) {
                return
            }
            const hostCalendar = this.getCalendarOrWarn(this.hostMember.attributes.calendar)
            if (hostCalendar && typeof hostCalendar.socketSendSessionQuery === 'function') {
                hostCalendar.socketSendSessionQuery()
            }
        },
        getCalendarOrWarn(calendarId) {
            const calendar = this.calendarsIndexed[calendarId]

            if (!calendar) {
                console.warn(`No calendar loaded for id ${calendarId} yet -- ignoring`)
            }

            return calendar
        },
        startOnHost() {
            const hostCalendar = this.getCalendarOrWarn(this.hostMember.attributes.calendar)
            if (!hostCalendar) return
            this.startSessionTimer()
            hostCalendar.socketSendSignal('SESSION_START')
        },
        startOn(calendarId) {
            const calendar = this.getCalendarOrWarn(calendarId)
            if (!calendar) return
            this.startSessionTimer()
            calendar.socketSendSignal('SESSION_START')
        },
        endOn(calendarId) {
            const calendar = this.getCalendarOrWarn(calendarId)
            if (!calendar) return
            calendar.socketSendSignal('SESSION_EXIT')
        },
        endOnAll() {
            for (let calendar of this.calendars) {
                calendar.socketSendSignal('SESSION_EXIT')
            }
        },
        endOnAllForce() {
            for (let calendar of this.calendars) {
                calendar.socketSendSignal('SESSION_EXIT_FORCE')
            }
        },
        reloadOnAll() {
            for (let calendar of this.calendars) {
                calendar.socketSendSignal('RELOAD')
            }
        },
        partyDisplayMultiplayerBrief() {
            for (let calendar of this.calendars) {
                calendar.socketSendSignal('MP_BRIEF')
            }
        },
        wakeAll() {
            for (let calendar of this.calendars) {
                calendar.socketSendSignal('WAKE')
            }
        },
        reloadOn(calendarId) {
            const calendar = this.getCalendarOrWarn(calendarId)
            if (!calendar) return
            calendar.socketSendSignal('RELOAD')
        },
        setCarOnAll(car) {
            for (let member of this.members) {
                member.entry.car = car
            }
        },
        setAssistsOnAll(assist) {
            for (let member of this.members) {
                member.entry.assists = assist
            }
        },
        setCarAndAssistsOnAll(car, assist) {
            for (let member of this.members) {
                member.entry.car = car
                member.entry.assists = assist
            }
        },
        acInGameGridOnAll() {
            for (let calendar of this.calendars) {
                calendar.socketSendSignal('AC_GRID')
            }
        },
        acInGamePitOnAll() {
            for (let calendar of this.calendars) {
                calendar.socketSendSignal('AC_PIT')
            }
        },
        acInGameGridOn(calendarId) {
            const calendar = this.getCalendarOrWarn(calendarId)
            if (!calendar) return
            calendar.socketSendSignal('AC_GRID')
        },
        acInGamePitOn(calendarId) {
            const calendar = this.getCalendarOrWarn(calendarId)
            if (!calendar) return
            calendar.socketSendSignal('AC_PIT')
        },
        acInGameRestartOn(calendarId) {
            const calendar = this.getCalendarOrWarn(calendarId)
            if (!calendar) return
            calendar.socketSendSignal('AC_RESTART')
        },
        async randomCar(member, extraFilters = {}) {
            const filters = Object.assign({ ordering: '?', page_size: 1, noCache: Math.random() }, extraFilters)
            const page = await CarResource.filter(filters)
            member.entry.car = page.resources[0].id
        },
        async randomSkin(member, extraFilters = {}) {
            const filters = Object.assign({ ordering: '?', page_size: 1, noCache: Math.random(), car: member.entry.car }, extraFilters)
            const page = await CarSkinResource.filter(filters)
            member.entry.skin = page.resources[0].id
            member.entry.skin_name = String(page.resources[0])

            this.$bvToast.toast(`Skin set to "${member.entry.skin_name}" on ${member.entry.readonly_values.calendar_name}`, {
                title: 'Set Skin',
                variant: 'success',
            })

            this.onFormChange()
        },
        async getSeatedData() {
            const calendarData = {}
            const promises = this.calendars.map((calendar) => calendar.seated().then((seatedData) => (calendarData[calendar.id] = seatedData)))
            await Promise.all(promises)
            this.seatedData = calendarData
        },
        getEntryForCalendar(calendarId) {
            return this.entries.find((entry) => entry.calendar == calendarId)
        },
        getMemberState(calendarId) {
            const state = this.memberStates[calendarId]

            if (state) {
                return state
            }

            return {}
        },
        setMemberState(calendarId, state) {
            this.memberStates[calendarId] = state
        },
        memberStatesHaveErrors() {
            const states = Object.values(this.memberStates)

            for (let state of states) {
                if (!state.selectedCarExists || !state.selectedTrackExists) {
                    return true
                }
            }

            return false
        },
        async membershipDestroy(member) {
            const calendarId = member.attributes.calendar
            await member.delete()
            this.reloadOn(member.attributes.calendar)
            this.$emit('boot', calendarId)
        },
        async partyDestroy() {
            this.resetSessionTimer()
            await this.party.delete()
            this.reloadOnAll()
            this.$emit('deleteParty')
        },
        memberCanBeReplacedBySeated(member) {
            const current = this.seatedData[member.attributes.calendar]?.current
            const currentUser = current?.user
            const selectedUser = member.entry?.user

            if (!currentUser || current?.is_blackout) {
                return false
            }

            return !selectedUser || currentUser != selectedUser
        },
        onFormChange() {
            // this.sessionDurations = this.getSessionDurations()
            this.hasChanges = true
            this.$emit('change', this.event)
        },
        onCalendarMessage(calendarResource, eventData) {
            // const member = this.members.find((member) => member.attributes.calendar == calendarResource.id)

            // if (eventData.type == 'signal' && eventData.value == 'SESSION_STARTED') {
            //     member.state = true
            // } else if (eventData.type == 'signal' && eventData.value == 'SESSION_STOPPED') {
            //     member.state = null
            // }
            if (eventData.type == 'status') {
                const member = this.members.find((member) => member.attributes.calendar == calendarResource.id)
                this.setMemberState(member.attributes.calendar, eventData.value)
            }
        },
        validate() {
            const errors = []

            if (!this.event.attributes.track) {
                errors.push('Track is required')
            }

            for (let entry of this.entries) {
                if (!entry.car) {
                    errors.push(`${entry.readonly_values.calendar_name}: Car is required`)
                }
            }

            if (errors.length) {
                for (let err of errors) {
                    this.addMessage('danger', err)
                }

                return false
            }

            return true
        },
        setUser(calendarId, userId) {
            const member = this.members.find((member) => (member.calendar = calendarId))
            member.entry.user = userId
        },
        getCarFilters(searchValue) {
            return { popular: true }
        },
        getTrackFilters(searchValue) {
            return { popular: true }
        },
        presetLoadForUser(userId, calendarId, presetId = null) {
            const userPresets = this.userPresetsIndexed[userId]

            if (!userPresets || userPresets.length < 1) {
                return false
            }

            const entry = this.getEntryForCalendar(calendarId)
            const userPreset = presetId ? userPresets.find((preset) => preset.id == presetId) : userPresets[0]
            const member = this.members.find((member) => member.attributes.calendar == calendarId)
            const isHost = member.attributes.is_host

            for (let key of ['car', 'skin', 'assists']) {
                const value = userPreset.attributes[key]

                if ('undefined' !== typeof value && value !== null) {
                    entry[key] = value
                }
            }

            if (isHost) {
                for (let key of ['track', 'penalties', 'fixed_setup', 'drift_mode']) {
                    const value = userPreset.attributes[key]

                    if ('undefined' !== typeof value && value !== null) {
                        this.event.set(key, userPreset.attributes[key])
                    }
                }

                const weather = userPreset.attributes.weather
                if ('undefined' !== typeof weather && weather !== null) {
                    this.event.set('preset_weather', weather)
                }

                if (userPreset.attributes.session_data && userPreset.attributes.session_data.length > 0) {
                    const sessionData = userPreset.attributes.session_data

                    for (let session of sessionData) {
                        session.event = this.event.id
                    }

                    this.sessions = sessionData
                }
            }

            this.$bvToast.toast(`Loaded preset "${userPreset}"`, {
                title: 'Preset',
                variant: 'success',
            })
        },
        presetLoadForHost(presetId = null) {
            const entryForHost = this.getEntryForCalendar(this.hostMember.attributes.calendar)
            return this.presetLoadForUser(entryForHost.user, entryForHost.calendar, presetId)
        },
        async fetchLoggedInUserPresets() {
            if (!this.userResource) return
            const pageData = await RacePresetUserResource.filter({ user: this.userResource.id })
            this.loggedInUserPresets = pageData.resources
        },
        presetsOpenLoggedInUserModal() {
            this.loggedInUserPresetsModalVisible = true
        },
        onLoggedInUserPresetSelect(preset) {
            const calendarId = this.party ? this.hostMember.attributes.calendar : this.calendarId
            if (!this.userPresetsIndexed[this.userResource.id]) {
                this.userPresets = [].concat(this.userPresets).concat(this.loggedInUserPresets)
            }
            this.presetLoadForUser(this.userResource.id, calendarId, preset.id)
            this.loggedInUserPresetsModalVisible = false
        },
        async fetchPresetsForUser(userId, isHost = false) {
            if (this.userPresetsIndexed[userId]) {
                if (isHost) this.userPresetsExistForHost = true
                return
            }

            const memberPresetsPageData = await RacePresetUserResource.filter({ user: userId })
            const presetResources = memberPresetsPageData.resources

            if (presetResources.length > 0) {
                this.userPresets = [].concat(this.userPresets).concat(presetResources)
                this.userPresetsExistForHost = isHost
            } else {
                this.userPresetsExistForHost = false
            }
        },
        presetsOpenUserEditModal(userId, calendarId) {
            this.userPresetsEditingUser = userId
            this.userPresetsEditingCalendarId = calendarId
        },
        onPresetsModalClose() {
            this.userPresetsEditingUser = null
            this.userPresetsEditingCalendarId = null
        },
        onPresetModalSelect(preset) {
            this.presetLoadForUser(this.userPresetsEditingUser, this.userPresetsEditingCalendarId, preset.id)
            this.onPresetsModalClose()
        },
        async onUserChange(userId, member) {
            if (!userId) {
                this.usersChanged = true
                this.onFormChange()
                return false
            }

            this.usersChanged = true
            await this.fetchPresetsForUser(userId, member.attributes.is_host)

            this.onFormChange()
        },
        async save() {
            if (!this.event) {
                return false
            }

            this.event.changes.entries = this.entries

            if (!this.hostHasControl) {
                this.event.changes.sessions = this.sessions
            }

            await this.event.save()
            this.hasChanges = false

            if (this.usersChanged) {
                this.resetSessionTimer()
                this.usersChanged = false
            }

            this.$emit('save')
        },
        startSessionTimer() {
            const data = { eventId: this.event.id, startedAt: Date.now() }
            localStorage.setItem('raceSessionTimer', JSON.stringify(data))
            this.sessionStartedAt = data.startedAt
            this.tickSessionTimer()
        },
        loadSessionTimer() {
            const stored = localStorage.getItem('raceSessionTimer')
            if (!stored) return
            const data = JSON.parse(stored)
            if (data.eventId === this.event.id) {
                this.sessionStartedAt = data.startedAt
                this.tickSessionTimer()
            }
        },
        tickSessionTimer() {
            clearInterval(this.sessionElapsedIntervalId)
            this.sessionElapsed = Date.now() - this.sessionStartedAt
            this.sessionElapsedIntervalId = setInterval(() => {
                this.sessionElapsed = Date.now() - this.sessionStartedAt
            }, 1000)
        },
        resetSessionTimer() {
            localStorage.removeItem('raceSessionTimer')
            clearInterval(this.sessionElapsedIntervalId)
            this.sessionStartedAt = null
            this.sessionElapsed = 0
        },
    },
    async mounted() {
        await this.load()
    },
    beforeDestroy() {
        // Only close calendar sockets here -- this component acquired those itself via
        // calendar.socketConnect() in load(). It never acquires this.event's socket (that's
        // done by whichever parent passed the event prop down, e.g. PartyDetail.vue /
        // CalendarControl.vue / AdminRaceControl.vue's edit-event modal), so it must not be
        // the one releasing it either -- this component can unmount independently of that
        // parent (e.g. AdminRaceControl.vue's edit modal closing) while the parent is still
        // using the same event socket.
        for (let calendar of this.calendars) {
            calendar.socketClose()
        }

        clearInterval(this.seatedDataIntervalId)
        clearInterval(this.sessionElapsedIntervalId)
    },
}
</script>

<template>
    <div>
        <messages />
        <b-form v-if="!loading" @submit.prevent="save()" @change="onFormChange()">
            <slot />
            <b-component :is="tabs ? 'b-tabs' : 'div'" ref="eventTabs" @activate-tab="onTabActivated">
                <b-component :is="tabs ? 'b-tab' : 'div'" data-tab-role="sessions" title="Sessions" :class="{ 'pt-3': tabs }" v-if="!hostHasControl">
                    <slot name="event">
                        <b-card body-class="py-2 pt-3" class="mb-3">
                            <b-form-group label="Circuit" label-class="font-weight-bold">
                                <resource-search-input :to="resources.TrackResource" v-model="event.attributes.track" :filter="getTrackFilters" @input="onFormChange()" primary-key />
                            </b-form-group>

                            <template #footer>
                                <b-row>
                                    <b-col class="text-center">
                                        <b-form-group label="Pénalités" class="mb-0" title="Activer les pénalités. Les tours risquent de ne pas être enregistrés si cette option est désactivée" v-b-tooltip.hover>
                                            <b-form-checkbox switch v-model="event.attributes.penalties" />
                                        </b-form-group>
                                    </b-col>

                                    <b-col class="text-center">
                                        <b-form-group label="Réglages fixes" class="mb-0" title="Réglages fixes. Chaque voiture possède la même configuration par défaut pour le moteur, les pneus, la suspension, etc." v-b-tooltip.hover>
                                            <b-form-checkbox switch v-model="event.attributes.fixed_setup" />
                                        </b-form-group>
                                    </b-col>

                                    <b-col class="text-center">
                                        <b-form-group label="Mode drift" class="mb-0" title="Activer le mode drift. Les dérapages rapportent des points. Les enchaînements de dérapages augmentent votre multiplicateur de score" v-b-tooltip.hover>
                                            <b-form-checkbox switch v-model="event.attributes.drift_mode" />
                                        </b-form-group>
                                    </b-col>
                                </b-row>
                            </template>
                        </b-card>
                    </slot>

                    <slot name="sessions">
                        <b-card v-if="party" body-class="p-3" class="mb-3">
                            <event-form-sessions v-model="sessions" :event="event.id" @input="onFormChange()" @durations="(durations) => (sessionDurations = durations)" ref="sessionsForm" />

                            <template #footer v-if="sessionDurations || sessionStartedAt">
                                <div class="d-flex justify-content-between align-items-center">
                                    <div>
                                        <template v-if="sessionDurations">
                                            <span v-if="sessionDurations.minutes" title="Durée totale estimée de la course. Cette estimation est précise uniquement si vous avez récemment rechargé cet écran" v-b-tooltip.hover> {{ sessionDurations.minutes }} min / vers {{ sessionDurations.end }} </span>
                                            <span v-if="sessionDurations.minutes && sessionDurations.laps"> + </span>
                                            <span v-if="sessionDurations.laps"> {{ sessionDurations.laps }} tours </span>
                                            <b-icon icon="arrow-repeat" role="button" @click="$refs.sessionsForm.updateDurations()" />
                                        </template>
                                    </div>
                                    <div class="d-flex align-items-center">
                                        <template v-if="sessionStartedAt">
                                            <small class="text-muted mr-2">Démarrée à {{ sessionStartedAtDisplay }}</small>
                                            <span class="font-weight-bold mr-1">{{ sessionElapsedDisplay }}</span>
                                            <b-icon icon="arrow-repeat" role="button" @click="resetSessionTimer()" title="Réinitialiser le chronomètre" v-b-tooltip.hover />
                                        </template>
                                        <b-icon v-else icon="stopwatch" role="button" @click="startSessionTimer()" title="Démarrer le chronomètre" v-b-tooltip.hover />
                                    </div>
                                </div>
                            </template>
                        </b-card>

                        <b-card v-else header="Préréglage" body-class="py-2 pt-3" class="mb-3">
                            <b-form-group>
                                <resource-search-input :to="resources.RacePresetResource" v-model="event.attributes.preset" @input="onFormChange()" primary-key />
                            </b-form-group>
                        </b-card>
                    </slot>
                </b-component>

                <p v-else class="text-muted text-center">L’hôte contrôle la sélection du circuit et les sessions de course. Hébergez votre propre groupe pour afficher davantage de commandes ici</p>

                <b-component v-if="members.length > 0" :is="tabs ? 'b-tab' : 'div'" data-tab-role="racers" :class="{ 'pt-3': tabs }">
                    <template #title>
                        <span class="position-relative d-inline-block">
                            Pilotes ({{ members.length }})
                            <b-icon v-if="memberStatesHaveErrors()" icon="dot" animation="fade" scale="2" class="text-danger" />
                        </span>
                    </template>
                    <b-row>
                        <b-col>
                            <slot name="entries">
                                <div v-for="(member, i) in members" :key="i">
                                    <b-card
                                        v-if="member.entry && (!calendarOnly || (calendarOnly && calendarId && calendarId == member.attributes.calendar))"
                                        class="mb-3"
                                        :border-variant="getMemberState(member.attributes.calendar).selectedCarExists === false || getMemberState(member.attributes.calendar).selectedTrackExists === false ? 'danger' : ''"
                                    >
                                        <template slot="header">
                                            <b-row>
                                                <b-col>
                                                    <div class="pt-1">
                                                        <span :class="{ 'font-weight-bold': getMemberState(member.attributes.calendar).acsRunning }"
                                                            >{{ member.attributes.calendar_name }} <strong v-if="calendarId == member.attributes.calendar">(Vous)</strong>
                                                            <span v-if="!noControls" :class="{ 'text-success': getMemberState(member.attributes.calendar).acsRunning }"><b-icon icon="circle-fill" scale="0.5" /></span
                                                        ></span>
                                                        <span class="text-muted ml-2" v-if="member.attributes.is_host" :title="`${member.attributes.calendar_name} héberge la partie`" v-b-tooltip.hover><b-icon icon="hdd" /></span>
                                                    </div>
                                                </b-col>
                                                <b-col class="text-right">
                                                    <b-button-group v-if="!noControls">
                                                        <b-button variant="light" size="sm" @click="acInGameGridOn(member.attributes.calendar)" title="Grille" v-b-tooltip.hover><b-icon icon="download" rotate="180" /></b-button>
                                                        <b-button variant="light" size="sm" @click="acInGamePitOn(member.attributes.calendar)" title="Envoyer aux stands" v-b-tooltip.hover><b-icon icon="house" /></b-button>
                                                        <b-button variant="light" size="sm" @click="acInGameRestartOn(member.attributes.calendar)" title="Redémarrer le pilote" v-b-tooltip.hover><b-icon icon="arrow-repeat" /></b-button>
                                                        <b-dropdown size="sm" variant="light" right>
                                                            <template #button-content>
                                                                <b-icon icon="gear" />
                                                            </template>
                                                            <b-dropdown-header>Contrôle de course</b-dropdown-header>
                                                            <b-dropdown-item @click="startOn(member.attributes.calendar)" variant="success">{{ member.attributes.is_host ? 'Démarrer la session' : 'Démarrage manuel' }}</b-dropdown-item>
                                                            <b-dropdown-item @click="endOn(member.attributes.calendar)" variant="danger">{{ member.attributes.is_host ? 'Terminer la session' : 'Arrêt manuel' }}</b-dropdown-item>
                                                            <b-dropdown-item @click="reloadOn(member.attributes.calendar)">Recharger</b-dropdown-item>
                                                            <b-dropdown-divider />
                                                            <b-dropdown-header>Actions groupées</b-dropdown-header>
                                                            <b-dropdown-item @click="setAssistsOnAll(member.entry.assists)">Copier les aides vers les autres</b-dropdown-item>
                                                            <b-dropdown-item @click="setCarOnAll(member.entry.car)">Copier la voiture vers les autres</b-dropdown-item>
                                                            <b-dropdown-item @click="setCarAndAssistsOnAll(member.entry.car, member.entry.assists)">Copier la voiture et les aides vers les autres</b-dropdown-item>
                                                            <b-dropdown-divider />
                                                            <b-dropdown-item @click="membershipDestroy(member)" variant="danger">{{ member.attributes.is_host ? 'Dissoudre le groupe' : 'Exclure du groupe' }}</b-dropdown-item>
                                                        </b-dropdown>
                                                    </b-button-group>
                                                </b-col>
                                            </b-row>
                                        </template>
                                        <b-form-group class="font-weight-bold" :description="member.entry && member.entry.skin_name ? member.entry.skin_name : ''">
                                            <resource-search-input :to="resources.CarResource" v-model="member.entry.car" @input="onFormChange()" :filter="getCarFilters" placeholder="Voiture" primary-key>
                                                <template #addon="data">
                                                    <!-- <b-link @click="randomCar(member)" title="Random Car" v-b-tooltip.hover><b-icon icon="question" scale="1" /></b-link> -->
                                                    <b-dropdown size="sm" variant="link" toggle-class="p-0" no-caret>
                                                        <template #button-content>
                                                            <b-link title="Options de voiture aléatoire" v-b-tooltip.hover><b-icon icon="question" scale="1.2" /></b-link>
                                                        </template>
                                                        <b-dropdown-header>Voitures</b-dropdown-header>
                                                        <b-dropdown-item @click="randomCar(member, { groups: 'GT3' })">Voiture <strong>GT3</strong> aléatoire</b-dropdown-item>
                                                        <b-dropdown-item @click="randomCar(member, { groups: 'F1' })">Voiture de <strong>F1</strong> aléatoire</b-dropdown-item>
                                                        <b-dropdown-item @click="randomCar(member, { groups: 'STREET' })">Voiture de <strong>route</strong> aléatoire</b-dropdown-item>
                                                        <b-dropdown-item @click="randomCar(member, { id__startswith: 'ks_' })">Voiture <strong>Kunos</strong> aléatoire</b-dropdown-item>
                                                        <b-dropdown-divider />
                                                        <b-dropdown-header>Livrée</b-dropdown-header>
                                                        <b-dropdown-item @click="randomSkin(member)">Livrée aléatoire</b-dropdown-item>
                                                    </b-dropdown>
                                                    <b-link @click="randomSkin(member)" title="Livrée aléatoire" v-b-tooltip.hover><b-icon icon="wrench" scale="0.7" /></b-link>
                                                    <b-link @click="data.searchClear()"><b-icon icon="x" /></b-link>
                                                </template>
                                            </resource-search-input>
                                            <template #description v-if="getMemberState(member.attributes.calendar).selectedCarExists === false && !hasChanges">
                                                <span class="text-danger">Cette voiture n’est pas téléchargée sur ce simulateur.</span>
                                            </template>
                                        </b-form-group>
                                        <b-row>
                                            <b-col>
                                                <slot name="user" :member="member" :user="member.entry.user" :calendar="member.entry.calendar" :user-display-name="getEntryForCalendar(member.entry.calendar).readonly_values.display_name_full">
                                                    <b-form-group class="font-weight-bold">
                                                        <resource-search-input :to="resources.UserResource" v-model="member.entry.user" placeholder="Utilisateur" @input="(userId) => onUserChange(userId, member)" addon-forced primary-key>
                                                            <template #addon="data">
                                                                <b-dropdown v-if="userPresetsIndexed[member.entry.user] && userPresetsIndexed[member.entry.user].length > 0" size="sm" variant="link" toggle-class="p-0" no-caret>
                                                                    <template #button-content>
                                                                        <b-link title="Cet utilisateur possède des préréglages. Cliquez pour afficher les options" v-b-tooltip.hover><b-icon icon="collection-play" scale="0.8" /></b-link>
                                                                    </template>
                                                                    <b-dropdown-header>Préréglages</b-dropdown-header>
                                                                    <b-dropdown-item @click="presetLoadForUser(member.entry.user, member.entry.calendar)">Charger le préréglage le plus récent de l’utilisateur</b-dropdown-item>
                                                                    <b-dropdown-item @click="presetsOpenUserEditModal(member.entry.user, member.entry.calendar)">Afficher les préréglages de l’utilisateur</b-dropdown-item>
                                                                </b-dropdown>
                                                                <b-link @click="member.entry.user = seatedData[member.entry.calendar].current.user" v-if="memberCanBeReplacedBySeated(member)" title="Charger l’utilisateur qui a réservé ce simulateur" v-b-tooltip.hover
                                                                    ><b-icon icon="clock-history" scale="0.7"
                                                                /></b-link>
                                                                <b-link @click="data.searchClear()"><b-icon icon="x" :class="{ 'text-muted': !member.entry.user }" /></b-link>
                                                            </template>
                                                        </resource-search-input>
                                                    </b-form-group>
                                                </slot>
                                            </b-col>

                                            <b-col md="4">
                                                <b-form-group class="font-weight-bold">
                                                    <resource-search-input :to="resources.AssistResource" v-model="member.entry.assists" placeholder="Aides" @input="onFormChange()" primary-key />
                                                </b-form-group>
                                            </b-col>
                                        </b-row>

                                        <small class="text-danger mb-3" v-if="getMemberState(member.attributes.calendar).selectedTrackExists === false">Le circuit sélectionné n’est pas téléchargé sur ce simulateur.</small>
                                    </b-card>
                                </div>
                            </slot>
                        </b-col>
                    </b-row>
                </b-component>

                <b-component v-else-if="event.attributes.entries && event.attributes.entries.length > 0" :is="tabs ? 'b-tab' : 'div'" data-tab-role="racers" title="Pilotes" :class="{ 'pt-3': tabs }">
                    <b-card header="Pilotes" class="mb-3">
                        <b-form-group class="font-weight-bold">
                            <resource-search-input :to="resources.CarResource" v-model="event.attributes.entries[0].car" @input="onFormChange()" placeholder="Voiture" primary-key />
                        </b-form-group>
                        <b-row>
                            <b-col>
                                <b-form-group class="font-weight-bold">
                                    <resource-search-input :to="resources.UserResource" v-model="event.attributes.entries[0].user" placeholder="Utilisateur" @input="onFormChange()" primary-key />
                                </b-form-group>
                            </b-col>

                            <b-col>
                                <b-form-group class="font-weight-bold">
                                    <resource-search-input :to="resources.AssistResource" v-model="event.attributes.entries[0].assists" placeholder="Aides" @input="onFormChange()" primary-key />
                                </b-form-group>
                            </b-col>
                        </b-row>
                    </b-card>
                </b-component>

                <b-component :is="tabs ? 'b-tab' : 'div'" title="Conditions" :class="{ 'pt-3': tabs }" v-if="!hostHasControl">
                    <b-card body-class="py-2 pt-3" class="mb-3">
                        <b-form-group label="Préréglage météo" label-class="font-weight-bold">
                            <resource-search-input :to="resources.WeatherPresetResource" v-model="event.attributes.preset_weather" @input="onFormChange()" primary-key />
                        </b-form-group>
                    </b-card>
                </b-component>
            </b-component>

            <slot name="submit">
                <div class="text-right">
                    <b-button type="submit" variant="secondary" v-if="!noSave">Enregistrer l’événement</b-button>
                </div>
            </slot>
        </b-form>

        <b-modal :visible="!!userPresetsEditingCalendarId" title="Préréglages utilisateur" @hide="onPresetsModalClose()" hide-footer no-fade centered>
            <b-list-group flush>
                <b-list-group-item v-for="preset in userPresetsEditing" :key="preset.id" class="px-0">
                    <div class="d-flex justify-content-between align-items-start">
                        <div>
                            <div class="font-weight-bold">{{ preset }}</div>
                            <small v-if="preset.attributes.description" class="text-muted d-block mb-1">{{ preset.attributes.description }}</small>
                            <div>
                                <b-badge variant="secondary" class="mr-1">{{ preset.attributes.car_name || "N'importe quelle voiture" }}</b-badge>
                                <b-badge variant="secondary" class="mr-1">{{ preset.attributes.track_name || "N'importe quel circuit" }}</b-badge>
                                <b-badge v-if="preset.attributes.session_data && preset.attributes.session_data.length" variant="secondary">{{ preset.attributes.session_data.length }} sessions</b-badge>
                            </div>
                        </div>
                        <b-button variant="primary" size="sm" class="ml-3 flex-shrink-0" @click="onPresetModalSelect(preset)">Sélectionner</b-button>
                    </div>
                </b-list-group-item>
            </b-list-group>
        </b-modal>

        <b-modal v-model="loggedInUserPresetsModalVisible" title="Vos préréglages" hide-footer no-fade centered>
            <b-list-group flush>
                <b-list-group-item v-for="preset in loggedInUserPresets" :key="preset.id" class="px-0">
                    <div class="d-flex justify-content-between align-items-start">
                        <div>
                            <div class="font-weight-bold">{{ preset }}</div>
                            <small v-if="preset.attributes.description" class="text-muted d-block mb-1">{{ preset.attributes.description }}</small>
                            <div>
                                <b-badge variant="secondary" class="mr-1">{{ preset.attributes.car_name || "N'importe quelle voiture" }}</b-badge>
                                <b-badge variant="secondary" class="mr-1">{{ preset.attributes.track_name || "N'importe quel circuit" }}</b-badge>
                                <b-badge v-if="preset.attributes.session_data && preset.attributes.session_data.length" variant="secondary">{{ preset.attributes.session_data.length }} sessions</b-badge>
                            </div>
                        </div>
                        <b-button variant="primary" size="sm" class="ml-3 flex-shrink-0" @click="onLoggedInUserPresetSelect(preset)">Sélectionner</b-button>
                    </div>
                </b-list-group-item>
            </b-list-group>
        </b-modal>
    </div>
</template>

<style lang="scss"></style>
